import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { affiliatorGroupApi } from '@/services/subscriptionApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ArrowLeft, Edit, Trash2, Users, CheckCircle2, XCircle, DollarSign } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { AffiliatorGroup } from '@/types/subscription';

export const AffiliatorGroupDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [statusChangeAction, setStatusChangeAction] = useState<'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate' | null>(null);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeNotes, setStatusChangeNotes] = useState('');
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const { hasPermission } = useAdminAuth();
  const { toast } = useToast();

  const canEdit = hasPermission('edit', 'affiliatorGroup');
  const canApprove = hasPermission('approve', 'affiliatorGroup');
  const canSuspend = hasPermission('suspend', 'affiliatorGroup');
  const canTerminate = hasPermission('terminate', 'affiliatorGroup');

  // Use entity hook for fetching affiliator
  const {
    data: affiliator,
    isLoading,
    error,
    refetch,
  } = useEntity<AffiliatorGroup>({
    fetchFn: (affiliatorId: string) => affiliatorGroupApi.getById(affiliatorId),
    entityId: id,
    entityName: 'affiliatorGroup',
    enabled: !!id,
  });

  const handleStatusChange = (action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate') => {
    setStatusChangeAction(action);
    setStatusChangeReason('');
    setStatusChangeNotes('');
    setStatusChangeModalOpen(true);
  };

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async (params: {
      action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate';
      reason?: string;
      notes?: string;
    }) => {
      if (!id) throw new Error('Affiliator Group ID is required');
      const { action, reason, notes } = params;
      if (action === 'approve') {
        await affiliatorGroupApi.approve(id, { notes });
      } else if (action === 'reject') {
        await affiliatorGroupApi.reject(id, { reason: reason!, notes });
      } else if (action === 'suspend') {
        await affiliatorGroupApi.suspend(id, { reason: reason!, notes });
      } else if (action === 'reactivate') {
        await affiliatorGroupApi.reactivate(id, { notes });
      } else if (action === 'terminate') {
        await affiliatorGroupApi.terminate(id, { reason: reason!, notes });
      }
    },
    entityName: 'affiliatorGroup',
    operation: 'update',
    onSuccess: () => {
      setStatusChangeModalOpen(false);
      setStatusChangeAction(null);
      setStatusChangeReason('');
      setStatusChangeNotes('');
      refetch();
    },
  });

  // Payout mutation
  const payoutMutation = useMutation({
    mutationFn: async (params: { amount: number; paymentMethod: string; notes: string }) => {
      if (!id) throw new Error('Affiliator Group ID is required');
      await affiliatorGroupApi.processPayout(id, params);
    },
    entityName: 'affiliatorGroup',
    operation: 'update',
    onSuccess: () => {
      setPayoutModalOpen(false);
      setPayoutAmount('');
      setPayoutMethod('');
      setPayoutNotes('');
      refetch();
    },
  });

  const confirmStatusChange = async () => {
    if (!id || !statusChangeAction) return;
    
    if ((statusChangeAction === 'reject' || statusChangeAction === 'suspend' || statusChangeAction === 'terminate') && !statusChangeReason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Reason is required for this action',
      });
      return;
    }

    await statusChangeMutation.mutate({
      action: statusChangeAction,
      reason: statusChangeReason || undefined,
      notes: statusChangeNotes || undefined,
    });
  };

  const handlePayout = async () => {
    if (!id) return;
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please enter a valid payout amount',
      });
      return;
    }

    if (!payoutMethod.trim()) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Payment method is required',
      });
      return;
    }

    await payoutMutation.mutate({
      amount,
      paymentMethod: payoutMethod,
      notes: payoutNotes,
    });
  };

  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return <LoadingState message="Loading affiliator group..." />;
  }

  if (error || !affiliator) {
    return <ErrorState error={error || new Error('Affiliator group not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/affiliators">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5" />
              {affiliator.groupName}
            </h2>
            <p className="text-muted-foreground mt-1">Group Code: {affiliator.groupCode}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/affiliators/${affiliator.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canApprove && affiliator.status === 'PENDING' && (
            <Button variant="outline" onClick={() => handleStatusChange('approve')}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve
            </Button>
          )}
          {canApprove && affiliator.status === 'PENDING' && (
            <Button variant="outline" onClick={() => handleStatusChange('reject')}>
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </Button>
          )}
          {canSuspend && affiliator.status === 'ACTIVE' && (
            <Button variant="outline" onClick={() => handleStatusChange('suspend')}>
              Suspend
            </Button>
          )}
          {canSuspend && affiliator.status === 'SUSPENDED' && (
            <Button variant="outline" onClick={() => handleStatusChange('reactivate')}>
              Reactivate
            </Button>
          )}
          {canTerminate && affiliator.status !== 'TERMINATED' && (
            <Button variant="destructive" onClick={() => handleStatusChange('terminate')}>
              <Trash2 className="mr-2 h-4 w-4" />
              Terminate
            </Button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  affiliator.status === 'ACTIVE'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : affiliator.status === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : affiliator.status === 'SUSPENDED'
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}
              >
                {affiliator.status}
              </span>
            </div>
            {affiliator.pendingPayouts > 0 && (
              <Button variant="outline" onClick={() => setPayoutModalOpen(true)}>
                <DollarSign className="mr-2 h-4 w-4" />
                Process Payout
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(affiliator.totalEarnings, affiliator.preferredCurrency)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
            <DollarSign className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {formatCurrency(affiliator.pendingPayouts, affiliator.preferredCurrency)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Paid Out</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(affiliator.totalPaidOut, affiliator.preferredCurrency)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="contact">Contact Information</TabsTrigger>
          <TabsTrigger value="business">Business Details</TabsTrigger>
          <TabsTrigger value="commission">Commission Config</TabsTrigger>
          <TabsTrigger value="payment">Payment Settings</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding Status</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Group identification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={affiliator.groupCode} label="Group Code" />
                <div className="space-y-2">
                  <Label>Group Name</Label>
                  <p className="mt-1">{affiliator.groupName}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={affiliator.description || ''}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input value={affiliator.companyName} disabled />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Information Tab */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>Contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input value={affiliator.contactInformation.contactPerson} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input value={affiliator.contactInformation.contactEmail} disabled />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input value={affiliator.contactInformation.contactPhone || 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input value={affiliator.contactInformation.department || 'N/A'} disabled />
                </div>
              </div>

              {affiliator.contactInformation.alternateEmail && (
                <div className="space-y-2">
                  <Label>Alternate Email</Label>
                  <Input value={affiliator.contactInformation.alternateEmail} disabled />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Details Tab */}
        <TabsContent value="business" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
              <CardDescription>Business information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Business Type</Label>
                  <Input value={affiliator.businessType || 'N/A'} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Tax ID</Label>
                  <Input value={affiliator.taxId || 'N/A'} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Registration Number</Label>
                <Input value={affiliator.businessRegistrationNumber || 'N/A'} disabled />
              </div>

              {affiliator.businessAddress && (
                <div className="space-y-4">
                  <Label>Business Address</Label>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input value={affiliator.businessAddress.street1 || ''} placeholder="Street 1" disabled />
                    <Input value={affiliator.businessAddress.street2 || ''} placeholder="Street 2" disabled />
                    <Input value={affiliator.businessAddress.city || ''} placeholder="City" disabled />
                    <Input value={affiliator.businessAddress.state || ''} placeholder="State" disabled />
                    <Input value={affiliator.businessAddress.postalCode || ''} placeholder="Postal Code" disabled />
                    <Input value={affiliator.businessAddress.country || ''} placeholder="Country" disabled />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commission Config Tab */}
        <TabsContent value="commission" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Commission Configuration</CardTitle>
              <CardDescription>Profit share and commission settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Profit Share Type</Label>
                  <Input value={affiliator.profitShareType} disabled />
                </div>
                <div className="space-y-2">
                  <Label>
                    {affiliator.profitShareType === 'PERCENTAGE' ? 'Profit Share Percentage' : 'Fixed Commission Amount'}
                  </Label>
                  <Input
                    value={
                      affiliator.profitShareType === 'PERCENTAGE'
                        ? `${affiliator.profitSharePercentage}%`
                        : formatCurrency(affiliator.fixedCommissionAmount || 0, affiliator.preferredCurrency)
                    }
                    disabled
                  />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Commission Model</Label>
                  <Input value={affiliator.commissionModel} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Payment Frequency</Label>
                  <Input value={affiliator.paymentFrequency} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Minimum Payout Amount</Label>
                <Input
                  value={formatCurrency(affiliator.minimumPayoutAmount, affiliator.preferredCurrency)}
                  disabled
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings Tab */}
        <TabsContent value="payment" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Settings</CardTitle>
              <CardDescription>Payment configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Preferred Currency</Label>
                <Input value={affiliator.preferredCurrency || 'N/A'} disabled className="font-mono" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onboarding Status Tab */}
        <TabsContent value="onboarding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Onboarding Status</CardTitle>
              <CardDescription>Onboarding information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Onboarded At</Label>
                  <Input value={formatDate(affiliator.onboardedAt)} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Valid From</Label>
                  <Input value={formatDate(affiliator.validFrom)} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input value={formatDate(affiliator.validUntil)} disabled />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Audit Information</CardTitle>
              <CardDescription>Creation and modification history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>{formatDate(affiliator.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated: </span>
                  <span>{formatDate(affiliator.updatedAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version: </span>
                  <span>{affiliator.version || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By: </span>
                  <span>{affiliator.createdBy || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Updated By: </span>
                  <span>{affiliator.updatedBy || 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Change Modal */}
      <Dialog open={statusChangeModalOpen} onOpenChange={setStatusChangeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusChangeAction === 'approve' && 'Approve Affiliator'}
              {statusChangeAction === 'reject' && 'Reject Affiliator'}
              {statusChangeAction === 'suspend' && 'Suspend Affiliator'}
              {statusChangeAction === 'reactivate' && 'Reactivate Affiliator'}
              {statusChangeAction === 'terminate' && 'Terminate Affiliator'}
            </DialogTitle>
            <DialogDescription>
              {(statusChangeAction === 'reject' || statusChangeAction === 'suspend' || statusChangeAction === 'terminate')
                ? 'Please provide a reason for this action.'
                : 'You can add optional notes.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(statusChangeAction === 'reject' || statusChangeAction === 'suspend' || statusChangeAction === 'terminate') && (
              <div className="space-y-2">
                <Label>Reason *</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Enter reason..."
                  value={statusChangeReason}
                  onChange={(e) => setStatusChangeReason(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Enter notes..."
                value={statusChangeNotes}
                onChange={(e) => setStatusChangeNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChangeModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStatusChange} disabled={statusChangeMutation.isLoading}>
              {statusChangeMutation.isLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Modal */}
      <Dialog open={payoutModalOpen} onOpenChange={setPayoutModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payout</DialogTitle>
            <DialogDescription>
              Process a payout for this affiliator. Available balance: {formatCurrency(affiliator.pendingPayouts, affiliator.preferredCurrency)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={affiliator.pendingPayouts}
                placeholder="0.00"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Input
                placeholder="e.g., Bank Transfer, PayPal"
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Enter notes..."
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePayout} disabled={payoutMutation.isLoading}>
              {payoutMutation.isLoading ? 'Processing...' : 'Process Payout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliatorGroupDetailPage;
